import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  ArrowRight,
  CalendarDays,
  Search,
  MessageCircle,
  Check,
  Sparkles,
  CheckCircle2,
  Send,
  Star,
} from 'lucide-react';
import { Button } from '@/client/components/ui/button';
import { Input } from '@/client/components/ui/input';
import { Label } from '@/client/components/ui/label';
import { Seo } from '@/client/components/Seo';
import { api, ApiError } from '@/client/lib/api';
import { isLocale, type Locale } from '@/client/i18n';
import { TurnstileWidget } from '@/client/features/contact/TurnstileWidget';
import { cn } from '@/client/lib/utils';

type ConfigResponse = { turnstileSiteKey: string | null };

const MENTORING_EMAIL = 'academy@dajanarodriguez.com';

const PROCESS_STEPS_SK = [
  { title: 'Napíš mi', body: 'Vyplň formulár nižšie. Povedz mi, čo riešiš a čo by si z mentoringu chcela získať.' },
  { title: 'Spoznáme sa', body: 'Ozvem sa ti emailom. Dohodneme si krátky online hovor, kde si povieme, či sme správny match.' },
  { title: 'Vyberieš si balík', body: '4-týždňový alebo 3-mesačný program. Spolu si vyberieme to, čo ti najviac sedí.' },
  { title: 'Zaplatíš faktúru', body: 'Pošlem ti faktúru na email. Po jej úhrade máme rezervované všetky termíny.' },
  { title: 'Začíname pracovať', body: 'Stretávame sa pravidelne online. Krok za krokom budujeme to, na čom ti záleží.' },
];

const PROCESS_STEPS_CS = [
  { title: 'Napiš mi', body: 'Vyplň formulář níže. Řekni mi, co řešíš a co by sis z mentoringu chtěla získat.' },
  { title: 'Poznáme se', body: 'Ozvu se ti e-mailem. Domluvíme si krátký online hovor, kde si řekneme, jestli jsme správný match.' },
  { title: 'Vybereš si balík', body: '4-týdenní nebo 3-měsíční program. Spolu si vybereme to, co ti nejvíc sedí.' },
  { title: 'Zaplatíš fakturu', body: 'Pošlu ti fakturu na e-mail. Po její úhradě máme rezervované všechny termíny.' },
  { title: 'Začínáme pracovat', body: 'Sejdeme se pravidelně online. Krok za krokem budujeme to, na čem ti záleží.' },
];

type PackageId = 'package_4w' | 'package_3m' | 'vip_day' | 'unsure';

export function Mentoring() {
  const { t } = useTranslation();
  const params = useParams();
  const locale: Locale = isLocale(params.locale) ? params.locale : 'sk';

  return (
    <div>
      <Seo
        title={
          locale === 'cs'
            ? 'Mentoring 1:1 s Dajanou | Dajana Rodriguez Academy'
            : 'Mentoring 1:1 s Dajanou | Dajana Rodriguez Academy'
        }
        description={
          locale === 'cs'
            ? 'Dlouhodobý průvod s Dajanou. Pravidelné 1:1 schůzky, WhatsApp support, příprava konkrétně pro tebe. 4-týdenní nebo 3-měsíční program. Od 590 €.'
            : 'Dlhodobý sprievod s Dajanou. Pravidelné 1:1 stretnutia, WhatsApp support, príprava konkrétne pre teba. 4-týždňový alebo 3-mesačný program. Od 590 €.'
        }
        locale={locale}
      />

      {/* HERO */}
      <section className="relative">
        <div
          className="absolute top-10 -right-32 w-[28rem] h-[28rem] rounded-full pointer-events-none animate-float-orb"
          style={{
            background: 'radial-gradient(circle, rgba(193,167,226,0.40) 0%, transparent 65%)',
            filter: 'blur(40px)',
          }}
          aria-hidden
        />
        <div className="relative mx-auto max-w-6xl px-5 sm:px-6 lg:px-8 pt-12 sm:pt-16 pb-12">
          <div className="grid gap-10 lg:gap-16 lg:grid-cols-2 items-center">
            <div className="order-2 lg:order-1">
              <span className="inline-flex items-center gap-2 rounded-pill bg-white/70 backdrop-blur-md border border-glass-border-strong px-4 py-1.5 font-sans text-caption uppercase tracking-[0.18em] text-primary animate-fade-up shadow-glass-soft">
                <Sparkles size={12} />
                Mentoring
              </span>
              <h1
                className="mt-5 font-display text-[clamp(2.25rem,6vw,4rem)] leading-[1.05] tracking-tight text-primary-dark animate-fade-up"
                style={{ animationDelay: '70ms' }}
              >
                {locale === 'cs' ? 'Dlouhodobý průvod se mnou.' : 'Dlhodobý sprievod so mnou.'}
              </h1>
              <p
                className="mt-5 font-display text-body-large text-text-secondary leading-relaxed animate-fade-up"
                style={{ animationDelay: '140ms' }}
              >
                {locale === 'cs'
                  ? 'Pravidelná setkání, kde ti pomáhám postavit to, na čem ti záleží — značku, byznys, projekt, nebo sebe samu. Krok za krokem, týden za týdnem.'
                  : 'Pravidelné stretnutia, kde ti pomáham postaviť to, na čom ti záleží — značku, biznis, projekt, alebo seba samú. Krok za krokom, týždeň za týždňom.'}
              </p>
              <a
                href="#packages"
                className="inline-block mt-8 animate-fade-up"
                style={{ animationDelay: '210ms' }}
              >
                <Button size="lg">
                  {locale === 'cs' ? 'Prohlédni si balíky' : 'Pozri si balíky'}
                  <ArrowRight size={18} />
                </Button>
              </a>
            </div>
            <div className="order-1 lg:order-2 animate-fade-up" style={{ animationDelay: '140ms' }}>
              <img
                src="/photos/DR_Academy_mentoring_hero.jpg"
                alt={
                  locale === 'cs'
                    ? 'Dajana Varga Rodriguez — mentoring s Dajana Rodriguez Academy'
                    : 'Dajana Varga Rodriguez — mentoring s Dajana Rodriguez Academy'
                }
                loading="eager"
                decoding="async"
                className="w-full h-auto rounded-2xl object-cover aspect-[4/5] shadow-glass"
              />
            </div>
          </div>
        </div>
      </section>

      {/* PRE KOHO JE MENTORING */}
      <section className="relative py-16 sm:py-20">
        <div className="mx-auto max-w-3xl px-5 sm:px-6 lg:px-8">
          <div className="text-center">
            <p className="dr-eyebrow">{locale === 'cs' ? 'Pro koho' : 'Pre koho'}</p>
            <h2 className="mt-4 font-display text-h2 sm:text-h1 text-primary-dark">
              {locale === 'cs' ? 'Pro koho je mentoring' : 'Pre koho je mentoring'}
            </h2>
          </div>
          <div className="mt-8 dr-card-static p-7 sm:p-10 lg:p-12 font-display text-body-large text-text-secondary leading-relaxed space-y-5">
            <p>
              {locale === 'cs'
                ? 'Mentoring není rada k jedné otázce. Je to průvod při budování něčeho většího.'
                : 'Mentoring nie je rada k jednej otázke. Je to sprievod pri budovaní niečoho väčšieho.'}
            </p>
            <p>
              {locale === 'cs'
                ? 'Pro ženu, která ví, co chce postavit — značku, byznys, projekt, kariéru — a chce mít při sobě někoho, kdo to už jednou udělal. Aby nemusela tápat měsíce při otázkách, které umím zodpovědět za hodinu.'
                : 'Pre ženu, ktorá vie, čo chce postaviť — značku, biznis, projekt, kariéru — a chce mať pri sebe niekoho, kto to už raz urobil. Aby nemusela tápať mesiace pri otázkach, ktoré viem zodpovedať za hodinu.'}
            </p>
            <p>
              {locale === 'cs'
                ? 'Pro ženu, která potřebuje pravidelný rytmus. Ne jednorázovou radu. Pravidelná setkání, akční kroky, zpětnou vazbu. Aby se věci pohybovaly dopředu — týden za týdnem.'
                : 'Pre ženu, ktorá potrebuje pravidelný rytmus. Nie jednorazovú radu. Pravidelné stretnutia, akčné kroky, spätnú väzbu. Aby sa veci pohybovali dopredu — týždeň za týždňom.'}
            </p>
          </div>
        </div>
      </section>

      {/* ČO ZÍSKAŠ */}
      <section className="relative py-16 sm:py-20">
        <div className="mx-auto max-w-6xl px-5 sm:px-6 lg:px-8">
          <div className="text-center">
            <p className="dr-eyebrow">{locale === 'cs' ? 'Co získáš' : 'Čo získaš'}</p>
            <h2 className="mt-4 font-display text-h2 sm:text-h1 text-primary-dark">
              {locale === 'cs' ? 'Co ti dávám' : 'Čo ti dávam'}
            </h2>
          </div>
          <div className="mt-12 grid gap-5 sm:gap-6 md:grid-cols-3 stagger-children">
            <ValueCard
              Icon={CalendarDays}
              title={locale === 'cs' ? 'Přímý čas se mnou' : 'Priamy čas so mnou'}
              body={
                locale === 'cs'
                  ? 'Pravidelné 1:1 schůzky online. Žádné generické rady — celý čas pracujeme na tom, co potřebuješ ty.'
                  : 'Pravidelné 1:1 stretnutia online. Žiadne generické rady — celý čas pracujeme na tom, čo potrebuješ ty.'
              }
            />
            <ValueCard
              Icon={Search}
              title={locale === 'cs' ? 'Příprava konkrétně pro tebe' : 'Príprava konkrétne pre teba'}
              body={
                locale === 'cs'
                  ? 'Před každou schůzkou si projdu, kde jsi — tvůj web, IG, čísla. Přijdu připravená, čas využijeme naplno.'
                  : 'Pred každým stretnutím si pozriem, kde si — tvoj web, IG, čísla. Prídem pripravená, čas využijeme naplno.'
              }
            />
            <ValueCard
              Icon={MessageCircle}
              title={locale === 'cs' ? 'Podpora mezi schůzkami' : 'Podpora medzi stretnutiami'}
              body={
                locale === 'cs'
                  ? 'Když narazíš na otázku nebo překážku mezi schůzkami, napiš mi přes WhatsApp. Odpovím v pracovních dnech do 24 hodin.'
                  : 'Keď narazíš na otázku alebo prekážku medzi stretnutiami, napíš mi cez WhatsApp. Odpoviem v pracovných dňoch do 24 hodín.'
              }
            />
          </div>
        </div>
      </section>

      {/* AKO TO PREBIEHA */}
      <section className="relative py-16 sm:py-24">
        <div className="mx-auto max-w-5xl px-5 sm:px-6 lg:px-8">
          <div className="text-center">
            <p className="dr-eyebrow">{locale === 'cs' ? 'Proces' : 'Proces'}</p>
            <h2 className="mt-4 font-display text-h2 sm:text-h1 text-primary-dark">
              {locale === 'cs' ? 'Jak to probíhá' : 'Ako to prebieha'}
            </h2>
          </div>
          <ol className="mt-12 grid gap-5 sm:gap-6 md:grid-cols-2 lg:grid-cols-3 stagger-children">
            {(locale === 'cs' ? PROCESS_STEPS_CS : PROCESS_STEPS_SK).map((step, i) => (
              <li key={step.title} className="dr-card-static p-7 sm:p-8">
                <div className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-primary text-white font-display text-h4 leading-none">
                  {i + 1}
                </div>
                <h3 className="mt-5 font-display text-h4 text-primary-dark">{step.title}</h3>
                <p className="mt-3 font-sans text-body text-text-secondary leading-relaxed">
                  {step.body}
                </p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* PACKAGES */}
      <section id="packages" className="relative py-16 sm:py-24">
        <div className="mx-auto max-w-6xl px-5 sm:px-6 lg:px-8">
          <div className="text-center">
            <p className="dr-eyebrow">{locale === 'cs' ? 'Balíky' : 'Balíky'}</p>
            <h2 className="mt-4 font-display text-h2 sm:text-h1 text-primary-dark">
              {locale === 'cs' ? 'Vyber si balík' : 'Vyber si balík'}
            </h2>
          </div>
          <div className="mt-12 grid gap-5 sm:gap-6 lg:grid-cols-3 items-stretch">
            <PackageCard
              title={locale === 'cs' ? '4-týdenní program' : '4-týždňový program'}
              subtitle={locale === 'cs' ? 'Pro rychlý posun' : 'Pre rýchly posun'}
              price="590 € s DPH"
              features={
                locale === 'cs'
                  ? [
                      '4 schůzky × 1 hodina online',
                      '1 schůzka týdně',
                      'Příprava konkrétně pro tebe',
                      'Akční kroky po každé schůzce',
                      'WhatsApp support mezi schůzkami',
                    ]
                  : [
                      '4 stretnutia × 1 hodina online',
                      '1 stretnutie týždenne',
                      'Príprava konkrétne pre teba',
                      'Akčné kroky po každom stretnutí',
                      'WhatsApp support medzi stretnutiami',
                    ]
              }
              ctaLabel={locale === 'cs' ? 'Mám zájem' : 'Mám záujem'}
              ctaHref="#contact-form"
            />
            <PackageCard
              recommended
              title={locale === 'cs' ? '3-měsíční intenzivní' : '3-mesačný intenzívny'}
              subtitle={locale === 'cs' ? 'Pro vážnou změnu' : 'Pre serióznu zmenu'}
              price="1 590 € s DPH"
              priceNote={locale === 'cs' ? 'nebo 3× 530 € měsíčně' : 'alebo 3× 530 € mesačne'}
              features={
                locale === 'cs'
                  ? [
                      '12 schůzek × 1 hodina online',
                      '1 schůzka týdně',
                      'Příprava konkrétně pro tebe',
                      'Akční kroky po každé schůzce',
                      'WhatsApp support mezi schůzkami',
                    ]
                  : [
                      '12 stretnutí × 1 hodina online',
                      '1 stretnutie týždenne',
                      'Príprava konkrétne pre teba',
                      'Akčné kroky po každom stretnutí',
                      'WhatsApp support medzi stretnutiami',
                    ]
              }
              ctaLabel={locale === 'cs' ? 'Mám zájem' : 'Mám záujem'}
              ctaHref="#contact-form"
              ribbon={locale === 'cs' ? 'Nejoblíbenější' : 'Najobľúbenejší'}
            />
            <PackageCard
              title="VIP Day"
              subtitle={locale === 'cs' ? 'Intenzivní setkání osobně' : 'Intenzívne stretnutie osobne'}
              price={locale === 'cs' ? 'Na míru' : 'Na mieru'}
              features={[
                locale === 'cs'
                  ? 'Pro případy, kdy potřebuješ koncentrovaný čas na jedno velké téma — launch, rebranding, strategický posun.'
                  : 'Pre prípady, keď potrebuješ koncentrovaný čas na jednu veľkú tému — launch, rebranding, strategický posun.',
                locale === 'cs'
                  ? 'Setkáme se osobně na několik hodin, projdeme všechno, co je třeba.'
                  : 'Stretneme sa osobne na niekoľko hodín, prejdeme všetko, čo treba.',
                locale === 'cs'
                  ? 'Termín, místo a cena se domlouvají individuálně.'
                  : 'Termín, miesto a cena sa dohadujú individuálne.',
              ]}
              ctaLabel={locale === 'cs' ? 'Napiš mi' : 'Napíš mi'}
              ctaHref="#contact-form"
            />
          </div>
        </div>
      </section>

      <MentoringContactForm locale={locale} />
    </div>
  );
}

function ValueCard({
  Icon,
  title,
  body,
}: {
  Icon: typeof CalendarDays;
  title: string;
  body: string;
}) {
  return (
    <article className="dr-card p-7 sm:p-8 group">
      <div className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-primary/20 to-primary/5 border border-primary/15 text-primary mb-6 group-hover:scale-110 transition-transform duration-300">
        <Icon size={22} />
      </div>
      <h3 className="font-display text-h3 text-primary-dark">{title}</h3>
      <p className="mt-3 font-sans text-body text-text-secondary leading-relaxed">{body}</p>
    </article>
  );
}

function PackageCard({
  title,
  subtitle,
  price,
  priceNote,
  features,
  ctaLabel,
  ctaHref,
  recommended,
  ribbon,
}: {
  title: string;
  subtitle: string;
  price: string;
  priceNote?: string;
  features: string[];
  ctaLabel: string;
  ctaHref: string;
  recommended?: boolean;
  ribbon?: string;
}) {
  return (
    <article
      className={cn(
        'relative overflow-hidden rounded-2xl p-7 sm:p-9 flex flex-col',
        recommended
          ? 'bg-gradient-to-br from-primary to-primary-dark text-white shadow-glass border border-white/15'
          : 'bg-white/80 backdrop-blur-xl border border-glass-border-strong shadow-glass-soft',
      )}
    >
      {recommended && ribbon && (
        <span className="absolute top-5 right-5 inline-flex items-center gap-1 rounded-pill bg-white text-primary px-3 py-1 font-sans text-caption uppercase tracking-[0.14em] font-semibold shadow-glass-soft">
          <Star size={11} className="fill-primary" />
          {ribbon}
        </span>
      )}
      <h3
        className={cn(
          'font-display text-h3',
          recommended ? 'text-white' : 'text-primary-dark',
        )}
      >
        {title}
      </h3>
      <p
        className={cn(
          'mt-1 font-sans text-body-small',
          recommended ? 'text-white/85' : 'text-text-secondary',
        )}
      >
        {subtitle}
      </p>
      <div className="mt-6">
        <p
          className={cn(
            'font-display text-[2.2rem] leading-none',
            recommended ? 'text-white' : 'text-primary-dark',
          )}
        >
          {price}
        </p>
        {priceNote && (
          <p
            className={cn(
              'mt-1 font-sans text-caption',
              recommended ? 'text-white/75' : 'text-text-muted',
            )}
          >
            {priceNote}
          </p>
        )}
      </div>
      <ul className="mt-6 space-y-3 flex-1">
        {features.map((feat) => (
          <li key={feat} className="flex items-start gap-3">
            <span
              className={cn(
                'mt-1 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full',
                recommended ? 'bg-white text-primary' : 'bg-primary text-white',
              )}
            >
              <Check size={12} strokeWidth={3} />
            </span>
            <span
              className={cn(
                'font-sans text-body leading-relaxed',
                recommended ? 'text-white/90' : 'text-text-primary',
              )}
            >
              {feat}
            </span>
          </li>
        ))}
      </ul>
      <a href={ctaHref} className="inline-block mt-8">
        <Button variant={recommended ? 'secondary' : 'primary'} size="md" className="w-full">
          {ctaLabel}
          <ArrowRight size={16} />
        </Button>
      </a>
    </article>
  );
}

function MentoringContactForm({ locale }: { locale: Locale }) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [packageId, setPackageId] = useState<PackageId>('unsure');
  const [topic, setTopic] = useState('');
  const [about, setAbout] = useState('');
  const [link, setLink] = useState('');
  const [consent, setConsent] = useState(false);
  const [token, setToken] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const config = useQuery<ConfigResponse>({
    queryKey: ['public', 'config'],
    queryFn: () => api<ConfigResponse>('/api/v1/config'),
    staleTime: 1000 * 60 * 60,
  });

  const packageLabelSk: Record<PackageId, string> = {
    package_4w: '4-týždňový program',
    package_3m: '3-mesačný intenzívny',
    vip_day: 'VIP Day (osobne)',
    unsure: 'Zatiaľ neviem, poraď mi',
  };
  const packageLabelCs: Record<PackageId, string> = {
    package_4w: '4-týdenní program',
    package_3m: '3-měsíční intenzivní',
    vip_day: 'VIP Day (osobně)',
    unsure: 'Zatím nevím, poraď mi',
  };
  const labels = locale === 'cs' ? packageLabelCs : packageLabelSk;

  const submit = useMutation({
    mutationFn: () => {
      const composed = [
        `${locale === 'cs' ? 'Balík' : 'Balík'}: ${labels[packageId]}`,
        '',
        `${locale === 'cs' ? 'Co chce řešit:' : 'Čo chce riešiť:'}`,
        topic,
        '',
        `${locale === 'cs' ? 'O sobě:' : 'O sebe:'}`,
        about,
        link ? '' : null,
        link ? `${locale === 'cs' ? 'Web nebo Instagram:' : 'Web alebo Instagram:'} ${link}` : null,
      ]
        .filter((value) => value !== null)
        .join('\n');

      return api('/api/v1/contact', {
        method: 'POST',
        body: {
          name,
          email,
          phone: phone || null,
          topic: 'cooperation',
          message: composed,
          turnstileToken: token,
          source: 'mentoring',
        },
      });
    },
    onSuccess: () => {
      setSubmitted(true);
      setErrorMsg(null);
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        if (err.status === 429) {
          setErrorMsg(t('contact.error_rate_limited'));
        } else if (err.code === 'VALIDATION_FAILED') {
          setErrorMsg(t('contact.error_turnstile_or_validation'));
          setToken(null);
        } else {
          setErrorMsg(t('errors.generic'));
        }
      } else {
        setErrorMsg(t('errors.generic'));
      }
    },
  });

  if (submitted) {
    return (
      <section className="relative py-20 sm:py-24" id="contact-form">
        <div className="mx-auto max-w-2xl px-5 sm:px-6 lg:px-8">
          <div className="dr-card-strong p-10 sm:p-14 text-center animate-fade-up">
            <div className="inline-flex items-center gap-2 rounded-pill bg-success/10 border border-success/25 text-success px-3 py-1 font-sans text-caption font-medium">
              <CheckCircle2 size={12} />
              {locale === 'cs' ? 'Odesláno' : 'Odoslané'}
            </div>
            <h2 className="mt-4 font-display text-h2 text-primary-dark">
              {locale === 'cs' ? 'Děkuji.' : 'Ďakujem.'}
            </h2>
            <p className="mt-3 font-display text-body-large text-text-secondary leading-relaxed max-w-md mx-auto">
              {locale === 'cs'
                ? 'Ozvu se ti e-mailem do 48 hodin.'
                : 'Ozvem sa ti emailom do 48 hodín.'}
            </p>
          </div>
        </div>
      </section>
    );
  }

  // If Turnstile is not configured, surface a mailto fallback so the page
  // still has a working contact path.
  if (config.isSuccess && !config.data?.turnstileSiteKey) {
    return (
      <section className="relative py-20 sm:py-24" id="contact-form">
        <div className="mx-auto max-w-2xl px-5 sm:px-6 lg:px-8 text-center">
          <p className="dr-eyebrow">{locale === 'cs' ? 'Napiš mi' : 'Napíš mi'}</p>
          <h2 className="mt-4 font-display text-h2 sm:text-h1 text-primary-dark">
            {locale === 'cs' ? 'Napiš mi' : 'Napíš mi'}
          </h2>
          <p className="mt-5 font-display text-body-large text-text-secondary leading-relaxed">
            {locale === 'cs'
              ? 'Napiš mi e-mailem a já se ti ozvu do 48 hodin.'
              : 'Napíš mi emailom a ja sa ti ozvem do 48 hodín.'}
          </p>
          <a
            href={`mailto:${MENTORING_EMAIL}`}
            className="mt-8 inline-flex items-center gap-2 rounded-pill bg-gradient-to-br from-primary to-primary-dark text-white px-6 h-12 font-sans text-body-small font-medium shadow-[0_4px_16px_rgba(117,59,189,0.28)] hover:-translate-y-px hover:shadow-[0_8px_24px_rgba(117,59,189,0.38)] transition-all"
          >
            {MENTORING_EMAIL}
          </a>
        </div>
      </section>
    );
  }

  return (
    <section id="contact-form" className="relative py-16 sm:py-24">
      <div className="mx-auto max-w-3xl px-5 sm:px-6 lg:px-8">
        <div className="text-center">
          <p className="dr-eyebrow">{locale === 'cs' ? 'Napiš mi' : 'Napíš mi'}</p>
          <h2 className="mt-4 font-display text-h2 sm:text-h1 text-primary-dark">
            {locale === 'cs' ? 'Napiš mi' : 'Napíš mi'}
          </h2>
          <p className="mt-5 font-display text-body-large text-text-secondary leading-relaxed">
            {locale === 'cs'
              ? 'Řekni mi, co řešíš a co by sis z mentoringu chtěla získat. Ozvu se ti e-mailem do 48 hodin.'
              : 'Povedz mi, čo riešiš a čo by si z mentoringu chcela získať. Ozvem sa ti emailom do 48 hodín.'}
          </p>
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (!consent) {
              setErrorMsg(
                locale === 'cs'
                  ? 'Souhlas se zpracováním osobních údajů je povinný.'
                  : 'Súhlas so spracovaním osobných údajov je povinný.',
              );
              return;
            }
            if (!token) {
              setErrorMsg(t('contact.error_turnstile_required'));
              return;
            }
            submit.mutate();
          }}
          className="mt-10 dr-card-static p-6 sm:p-10 space-y-5"
        >
          <div className="grid gap-5 md:grid-cols-2">
            <div>
              <Label htmlFor="mentoring-name">
                {locale === 'cs' ? 'Jméno a příjmení' : 'Meno a priezvisko'}
              </Label>
              <Input
                id="mentoring-name"
                required
                minLength={2}
                maxLength={100}
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="mentoring-email">E-mail</Label>
              <Input
                id="mentoring-email"
                type="email"
                required
                maxLength={200}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div className="md:col-span-2">
              <Label htmlFor="mentoring-phone">
                {locale === 'cs' ? 'Telefon (volitelné)' : 'Telefón (voliteľné)'}
              </Label>
              <Input
                id="mentoring-phone"
                type="tel"
                maxLength={50}
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
              />
            </div>
          </div>

          <div>
            <Label htmlFor="mentoring-package">
              {locale === 'cs' ? 'O který balík máš zájem?' : 'O ktorý balík máš záujem?'}
            </Label>
            <select
              id="mentoring-package"
              value={packageId}
              onChange={(e) => setPackageId(e.target.value as PackageId)}
              className="mt-1.5 block w-full rounded-md bg-white/80 backdrop-blur-md border border-glass-border-strong h-12 px-4 font-sans text-body text-text-primary hover:border-primary/30 focus-visible:outline-none focus-visible:border-primary focus-visible:shadow-glow focus-visible:bg-white transition-all"
            >
              <option value="package_4w">{labels.package_4w}</option>
              <option value="package_3m">{labels.package_3m}</option>
              <option value="vip_day">{labels.vip_day}</option>
              <option value="unsure">{labels.unsure}</option>
            </select>
          </div>

          <div>
            <Label htmlFor="mentoring-topic">
              {locale === 'cs' ? 'Co chceš v mentoringu řešit?' : 'Čo chceš v mentoringu riešiť?'}
            </Label>
            <textarea
              id="mentoring-topic"
              required
              minLength={10}
              maxLength={500}
              rows={3}
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              placeholder={
                locale === 'cs'
                  ? 'Krátce mi řekni, na čem bys se mnou chtěla pracovat. Značka, byznys, mindset, něco jiného?'
                  : 'Krátko mi povedz, na čom by si chcela so mnou pracovať. Značka, biznis, mindset, niečo iné?'
              }
              className="block w-full rounded-md bg-white/80 backdrop-blur-md border border-glass-border-strong p-4 font-sans text-body text-text-primary placeholder:text-text-muted hover:border-primary/30 focus-visible:outline-none focus-visible:border-primary focus-visible:shadow-glow focus-visible:bg-white transition-all"
            />
          </div>

          <div>
            <Label htmlFor="mentoring-about">
              {locale === 'cs' ? 'Pár vět o tobě' : 'Pár viet o tebe'}
            </Label>
            <textarea
              id="mentoring-about"
              required
              minLength={10}
              maxLength={500}
              rows={3}
              value={about}
              onChange={(e) => setAbout(e.target.value)}
              placeholder={
                locale === 'cs'
                  ? 'Co děláš, kde jsi v podnikání nebo v životě, jaký máš cíl.'
                  : 'Čo robíš, kde si v podnikaní alebo v živote, aký máš cieľ.'
              }
              className="block w-full rounded-md bg-white/80 backdrop-blur-md border border-glass-border-strong p-4 font-sans text-body text-text-primary placeholder:text-text-muted hover:border-primary/30 focus-visible:outline-none focus-visible:border-primary focus-visible:shadow-glow focus-visible:bg-white transition-all"
            />
          </div>

          <div>
            <Label htmlFor="mentoring-link">
              {locale === 'cs' ? 'Web nebo Instagram (volitelné)' : 'Web alebo Instagram (voliteľné)'}
            </Label>
            <Input
              id="mentoring-link"
              type="text"
              maxLength={200}
              value={link}
              onChange={(e) => setLink(e.target.value)}
              placeholder={
                locale === 'cs'
                  ? 'Abych si prošla tvou situaci před naším hovorem (doporučuji).'
                  : 'Aby som si pozrela tvoju situáciu pred naším hovorom (odporúčam).'
              }
            />
          </div>

          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={consent}
              onChange={(e) => setConsent(e.target.checked)}
              className="mt-1 h-5 w-5 rounded border-glass-border-strong text-primary focus-visible:ring-2 focus-visible:ring-primary"
            />
            <span className="font-sans text-body-small text-text-secondary leading-relaxed">
              {locale === 'cs'
                ? 'Souhlasím se zpracováním osobních údajů pro účely vyřízení mého dotazu.'
                : 'Súhlasím so spracovaním osobných údajov na účely vybavenia môjho dopytu.'}
            </span>
          </label>

          <div>
            <TurnstileWidget
              siteKey={config.data?.turnstileSiteKey ?? null}
              onToken={setToken}
              onExpired={() => setToken(null)}
            />
          </div>

          {errorMsg && (
            <p
              className="rounded-2xl px-4 py-3 font-sans text-body-small text-error border"
              style={{ background: 'rgba(196,50,45,0.08)', borderColor: 'rgba(196,50,45,0.25)' }}
            >
              {errorMsg}
            </p>
          )}

          <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:justify-between pt-2">
            <p className="font-sans text-caption text-text-muted inline-flex items-start gap-1.5">
              <Sparkles size={11} className="text-primary mt-0.5 shrink-0" />
              {locale === 'cs' ? 'Ozvu se ti do 48 hodin.' : 'Ozvem sa ti do 48 hodín.'}
            </p>
            <Button type="submit" disabled={submit.isPending}>
              {submit.isPending ? t('common.loading') : (locale === 'cs' ? 'Odeslat' : 'Odoslať')}
              <Send size={16} />
            </Button>
          </div>
        </form>
      </div>
    </section>
  );
}
